/**
 * Credential vault rules the SPA and the façade must agree on (steward-app#2011).
 *
 * Three things live here because each is decided once and read on both sides of the API:
 * which categories a credential may carry, what counts as a recorded agreement to the
 * vault's terms, and what "looks like a payment card number" means.
 *
 * Background: Steward is not intended to hold payment card data, and the Terms of Service
 * say so (steward-site #60). The vault used to offer a `card` category, which invited the
 * thing the terms prohibit, and an `account-number` category; both were removed on
 * 2026-10-02 and the vault is positioned for logins and secure notes.
 */

/** The categories a credential may be created or saved with. */
export const CREDENTIAL_CATEGORY_VALUES = ['login', 'secure-note', 'other'] as const;

export type CredentialCategory = (typeof CREDENTIAL_CATEGORY_VALUES)[number];

export function isCredentialCategory(value: unknown): value is CredentialCategory {
  return typeof value === 'string' && (CREDENTIAL_CATEGORY_VALUES as readonly string[]).includes(value);
}

/**
 * Current substance version of the credential vault agreement.
 *
 * Bump ONLY when what the person agrees to materially changes (a new prohibition, a
 * weakened storage guarantee). Everyone holding an older version is asked again before
 * their next add or edit. A wording tweak does not bump it. The copy each version stood
 * for is the SPA's `CredentialConsent.tsx` at the commit that set the number.
 */
export const CREDENTIAL_CONSENT_VERSION = 1;

/**
 * The two preference keys that record an agreement. They are written ONLY by the façade's
 * `PUT /me`, from its `credentialConsent` field, with a time the façade stamps itself; it refuses a
 * `preferences` body that names either key. `me.test.ts` pins both halves.
 */
export const CREDENTIAL_CONSENT_ACCEPTED_AT_KEY = 'credentialConsentAcceptedAt';
export const CREDENTIAL_CONSENT_VERSION_KEY = 'credentialConsentVersion';
export const CREDENTIAL_CONSENT_PREFERENCE_KEYS = [
  CREDENTIAL_CONSENT_ACCEPTED_AT_KEY,
  CREDENTIAL_CONSENT_VERSION_KEY,
] as const;

/**
 * Has this person agreed to the CURRENT version of the vault's terms?
 *
 * Unlike the AI consent, an absent version is NOT read as version 1: no agreement was ever
 * recorded without one, so there is no older shape to honour.
 */
export function hasCredentialConsent(preferences: Record<string, unknown> | null | undefined): boolean {
  if (!preferences) return false;
  const at = preferences[CREDENTIAL_CONSENT_ACCEPTED_AT_KEY];
  const version = preferences[CREDENTIAL_CONSENT_VERSION_KEY];
  return typeof at === 'string' && at.length > 0
    && typeof version === 'number' && version >= CREDENTIAL_CONSENT_VERSION;
}

/** Groups of digits joined by single spaces or hyphens: `4111 1111 1111 1111 123` is one
 *  run of five groups. Anything else (a slash, a letter, two spaces) ends the run. */
const DIGIT_RUN = /\d+(?:[ -]\d+)*/g;

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (double) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * Does this text contain something shaped like a payment card number?
 *
 * A heuristic that drives a WARNING, never a refusal: 13 to 19 digits, starting 2 to 6
 * (the leading digits the major card networks issue under), passing the Luhn check, and
 * not one digit repeated. Roughly one in ten unrelated numbers of the right length passes
 * Luhn by chance, which is why the SPA lets the person save anyway.
 *
 * A candidate is one or more WHOLE neighbouring groups of a run, so a number typed with
 * its expiry or security code after it (`4111 1111 1111 1111 123`) is still found, while an
 * unbroken string of 20 or more digits is not searched for a card inside it. It can miss a
 * card number written another way (two spaces between groups, dots, digits spelled out).
 * Nothing may treat a `false` here as proof that no card number is present.
 *
 * Pure and local. The SPA calls it on a secret before submit; the value must never be
 * logged or sent anywhere for the check.
 */
export function looksLikePaymentCardNumber(text: string | null | undefined): boolean {
  if (!text) return false;
  for (const run of text.matchAll(DIGIT_RUN)) {
    const groups = run[0].split(/[ -]/);
    for (let start = 0; start < groups.length; start++) {
      let digits = '';
      for (let end = start; end < groups.length && digits.length + groups[end].length <= 19; end++) {
        digits += groups[end];
        if (digits.length < 13) continue;
        if (digits[0] < '2' || digits[0] > '6') continue;
        if (/^(\d)\1+$/.test(digits)) continue;
        if (passesLuhn(digits)) return true;
      }
    }
  }
  return false;
}
